import { CHANNELS } from "../../shared/channels";
import { SHARE_EXTRAS, SHARE_KINDS, ShareBoard, ShareExtra, ShareKind, SharePerson } from "../../shared/sharing";
import { call, listen } from "../bridge";
import { byId } from "../dom";
import { isViewActive, onViewShown } from "../navigation";
import { t } from "../i18n";
import { buzz, flash } from "../tweens";

// everyone's own sharing switches: per person, which of my things they can read.
// every switch saves straight away, the server only lets me change my own

let board: ShareBoard | null = null;
const open = new Set<string>();

function setStatus(text: string): void {
  const status = byId("sharing-status");
  status.textContent = text;
  flash(status);
}

function errorOf(error: unknown): string {
  return String(error instanceof Error ? error.message : error).replace(/^.*Error: /, "");
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = "", text = ""): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function myKinds(): ShareKind[] {
  return SHARE_KINDS.filter((kind) => board?.kinds.includes(kind.id));
}

function kindNames(ids: string[]): string {
  return SHARE_KINDS.filter((kind) => ids.includes(kind.id)).map((kind) => t(kind.label).toLowerCase()).join(", ");
}

function summaryLine(person: SharePerson): string {
  const shared = board?.mine[person.id] || [];
  const adds = SHARE_EXTRAS.filter((extra) => shared.includes(extra.id)).map((extra) => {
    const label = SHARE_KINDS.find((kind) => kind.id === extra.of)?.label;
    return label ? t(label).toLowerCase() : "";
  });
  const line = shared.length ? t("sees your {kinds}", { kinds: kindNames(shared) }) : t("sees nothing of yours");
  return adds.length ? t("{line}, adds to your {kinds}", { line, kinds: adds.join(", ") }) : line;
}

async function setShare(person: SharePerson, kind: ShareKind | ShareExtra, on: boolean): Promise<void> {
  buzz();
  try {
    board = await call<ShareBoard>(CHANNELS.sharesSet, person.id, kind.id, on);
    const extra = SHARE_EXTRAS.find((item) => item.id === kind.id);
    const ofLabel = SHARE_KINDS.find((item) => item.id === extra?.of)?.label;
    const of = ofLabel ? t(ofLabel).toLowerCase() : "";
    const label = t(kind.label).toLowerCase();
    const name = person.name;
    if (extra) setStatus(on ? t("{name} can add to your {kind}", { name, kind: of }) : t("{name} can only read your {kind}", { name, kind: of }));
    else setStatus(on ? t("{name} can see your {kind}", { name, kind: label }) : t("{kind} hidden from {name}", { name, kind: label }));
  } catch (error) {
    setStatus(errorOf(error));
  }
  render();
}

async function setAll(person: SharePerson, on: boolean): Promise<void> {
  buzz();
  const shared = board?.mine[person.id] || [];
  try {
    for (const kind of myKinds()) {
      if (shared.includes(kind.id) !== on) board = await call<ShareBoard>(CHANNELS.sharesSet, person.id, kind.id, on);
    }
    setStatus(on ? t("{name} can see everything you can share", { name: person.name }) : t("{name} sees nothing of yours now", { name: person.name }));
  } catch (error) {
    setStatus(errorOf(error));
  }
  render();
}

function kindRow(person: SharePerson, kind: ShareKind | ShareExtra): HTMLElement {
  const row = el("div", "perm-row");
  if ("of" in kind) row.classList.add("perm-sub");
  const text = el("div", "perm-text");
  text.append(el("strong", "", t(kind.label)), el("small", "", t(kind.detail)));
  const label = el("label", "toggle");
  label.setAttribute("aria-label", t("share {kind} with {name}", { kind: t(kind.label).toLowerCase(), name: person.name }));
  const box = el("input");
  box.type = "checkbox";
  box.checked = (board?.mine[person.id] || []).includes(kind.id);
  box.addEventListener("change", () => void setShare(person, kind, box.checked));
  const track = el("span", "toggle-track");
  track.append(el("i"));
  label.append(box, track);
  row.append(text, label);
  return row;
}

function personCard(person: SharePerson): HTMLElement {
  const card = el("details", "panel person");
  card.open = open.has(person.id);
  card.addEventListener("toggle", () => {
    if (card.open) open.add(person.id);
    else open.delete(person.id);
  });
  const summary = el("summary", "person-head");
  summary.append(el("span", "person-name", person.name), el("span", "person-meta", summaryLine(person)));
  const body = el("div", "person-body");
  const kinds = myKinds();
  if (!kinds.length) {
    body.append(el("p", "empty", t("you don't have notes, pings or anything else to share yet")));
  } else {
    const all = el("div", "sharing-all");
    const everything = el("button", "pill small", t("share all"));
    everything.type = "button";
    everything.addEventListener("click", () => void setAll(person, true));
    const nothing = el("button", "pill small", t("share none"));
    nothing.type = "button";
    nothing.addEventListener("click", () => void setAll(person, false));
    all.append(everything, nothing);
    const rows = el("div", "perm-list");
    const shared = board?.mine[person.id] || [];
    rows.append(...kinds.flatMap((kind) => [
      kindRow(person, kind),
      ...SHARE_EXTRAS.filter((extra) => extra.of === kind.id && shared.includes(kind.id)).map((extra) => kindRow(person, extra))
    ]));
    body.append(all, rows);
  }
  card.append(summary, body);
  return card;
}

function render(): void {
  if (!board) return;
  const list = byId("sharing-list");
  if (!board.people.length) list.replaceChildren(el("p", "empty", t("nobody else is in chix yet")));
  else list.replaceChildren(...board.people.map(personCard));
  const withMe = byId("sharing-with-me");
  withMe.replaceChildren(...board.withMe.map((person) => {
    const row = el("li");
    row.append(el("strong", "", person.name), el("small", "", t("shares their {kinds}", { kinds: kindNames(person.kinds) })));
    return row;
  }));
  byId("sharing-with-me-empty").hidden = board.withMe.length > 0;
}

async function reload(): Promise<void> {
  try {
    board = await call<ShareBoard>(CHANNELS.sharesGet);
    render();
  } catch (error) {
    setStatus(errorOf(error));
  }
}

export function setupSharingView(): void {
  byId("sharing-refresh").addEventListener("click", () => void reload());
  listen(CHANNELS.sharesUpdated, () => {
    if (isViewActive("sharing")) void reload();
  });
  onViewShown("sharing", () => void reload());
}
